import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaderResponse,
  HttpInterceptor,
  HttpProgressEvent,
  HttpRequest,
  HttpResponse,
  HttpUserEvent
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ErrorLogin } from '@shared/enum/error-login.enum';
import { AuthResponse } from '@shared/models/auth.model';
import { ErrorResponse } from '@shared/models/error.model';
import { AuthService } from '@shared/services/auth.service';
import { SidebarRefService } from '@shared/services/sidebar-ref.service';
import { LayoutActionErrorUpdate } from '@shared/store/actions/layout.action';
import { AppStates } from '@shared/store/state/app.state';
import { CookieService } from 'ngx-cookie-service';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, Observable, TimeoutError, throwError, timeout } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';

import { environment as env } from '../../../environments/environment';
import { ClientIdTypeEnum } from '../enum/client-id.enum';
import { LogInFailureAction, LogInSuccessAction } from '../store/actions/auth.actions';
import { ClientIdAction } from '../store/actions/user-info.action';
import { CookiesUtil } from '../utils/cookies-util';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private refreshTokenInProgress = false;
  // Refresh Token Subject tracks the current token, or is null if no token is currently
  // available (e.g. refresh pending).
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  private timeOutSec = 3 * 1000;

  constructor(
    protected readonly authService: AuthService,
    protected cookieService: CookieService,
    protected readonly store: Store<AppStates>,
    protected readonly logger: NGXLogger,
    protected router: Router,
    private sidebarRefService: SidebarRefService,
    protected cookiesUtil: CookiesUtil = new CookiesUtil(cookieService)
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<
    HttpEvent<any> | HttpHeaderResponse | HttpProgressEvent | HttpResponse<any> | HttpUserEvent<any> | any
  > {
    return next.handle(this.addAuthenticationToken(request)).pipe(
      catchError((error) => {
        const refreshTokenCookiesName = this.cookiesUtil.generateNameByAmount(env.cookies.refreshToken);
        const refreshToken = this.cookiesUtil.concatCookie(refreshTokenCookiesName);
        const hasToken = !refreshToken && !request.headers.has('Authorization');
        const isIgnoreService = request.url.includes('oauth');

        if (isIgnoreService || hasToken) {
          // We do another check to see if refresh token failed
          // In this case we need to delete all authentication cookies and redirect to login page
          if (!refreshToken) {
            // In case of empty refresh-token
            error.message = 'Refresh token in cookie is empty.';
            this.logger.info(`Refresh Token in catchError ::`, error);
            this.sidebarRefService.close();
          }

          // note: forceChangePassword has validation token expired in component
          if (!this.router.routerState.snapshot.url.includes('/change-password')) {
            this.deleteTokenCookie();
            // this.store.dispatch(new HideAllModalAction(true));

            this.store.dispatch(new ClientIdAction(env.defaultClientId as ClientIdTypeEnum));
            this.store.dispatch(new LayoutActionErrorUpdate(false));
            this.store.dispatch(new LogInFailureAction(error as ErrorResponse));
          }
        } else if (error && error instanceof HttpErrorResponse) {
          if (error.status && error.status === 401) {
            const wwwAuthHeader = error.headers.get('WWW-authenticate');

            if ((wwwAuthHeader && wwwAuthHeader.includes('Jwt expired')) || wwwAuthHeader === 'Bearer') {
              if (this.refreshTokenInProgress) {
                return this.refreshTokenSubject.pipe(
                  filter(Boolean),
                  take(1),
                  switchMap(() => next.handle(this.addAuthenticationToken(request)))
                );
              }

              this.refreshTokenInProgress = true;

              // Set the refreshTokenSubject to null so that subsequent API calls will wait
              // until the new token has been retrieved
              this.refreshTokenSubject.next(null);

              this.logger.info(`set refresh token time out ${this.timeOutSec} ms`);

              return this.authService.refreshToken(refreshToken).pipe(
                timeout(this.timeOutSec),
                switchMap((credential: AuthResponse) => {
                  // After got new refresh token
                  // Then, set new credential to local cookies
                  this.store.dispatch(new LogInSuccessAction(credential));

                  this.logger.info(`refreshed token`);

                  // When the call to refreshToken completes we reset the refreshTokenInProgress to false
                  // for the next time the token needs to be refreshed
                  this.refreshTokenInProgress = false;
                  this.refreshTokenSubject.next(credential.access_token);

                  return next.handle(this.addAuthenticationToken(request));
                }),
                catchError((err) => {
                  this.logger.info('Refresh Token :: catchError', err);

                  if (err.url.includes('/oauth/token')) {
                    this.sidebarRefService.close();
                  }

                  // Only oauth path will delete cookies and navigate to login page
                  if (err instanceof TimeoutError || (err.payload && err.payload.url.includes('oauth'))) {
                    this.deleteTokenCookie();
                    // this.store.dispatch(new HideAllModalAction(true));

                    if (err instanceof TimeoutError) {
                      err.message = ErrorLogin.ERROR_REFRESH_TOKEN_TIME_OUT;
                      this.sidebarRefService.close();
                    }

                    // note: forceChangePassword has validation token expired in component
                    if (err.payload && !this.router.routerState.snapshot.url.includes('/change-password')) {
                      this.store.dispatch(new ClientIdAction(env.defaultClientId as ClientIdTypeEnum));
                      this.store.dispatch(new LayoutActionErrorUpdate(false));
                      this.store.dispatch(new LogInFailureAction(err));
                    }
                  }
                  this.refreshTokenInProgress = false;

                  return throwError(err);
                })
              );
            }
          }
        }

        this.logger.info(`token in catchError ::`, error);
        return throwError(error);
      })
    );
  }

  addAuthenticationToken(request) {
    const accessTokenCookiesName = this.cookiesUtil.generateNameByAmount(env.cookies.accessToken);
    const accessToken = this.cookiesUtil.concatCookie(accessTokenCookiesName);
    const isEmptyToken = !accessToken;
    const isIgnoreService =
      request.url.includes('oauth') ||
      request.url.includes('/assets') ||
      // relay เป็น service ในเครื่องผู้ใช้ ไม่ใช่ API ของเรา — ส่ง token ไปคือปล่อยให้ process ในเครื่องอ่านได้
      // และ header ที่เพิ่มมาทำให้เบราว์เซอร์ยิง preflight ซึ่ง relay ไม่ได้รองรับ
      request.url.startsWith(env.relayPrinterUrl);

    if (isIgnoreService || isEmptyToken) {
      this.logger.info(` Token have not been added in addAuthenticationToken ::`, request);
      return request;
    }

    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${accessToken}`
      }
    });
  }

  deleteTokenCookie() {
    const accessTokenCookiesName = this.cookiesUtil.generateNameByAmount(env.cookies.accessToken);
    const refreshTokenTokenCookiesName = this.cookiesUtil.generateNameByAmount(env.cookies.refreshToken);

    Array.isArray(accessTokenCookiesName)
      ? accessTokenCookiesName.map((value) => {
          this.cookieService.delete(value);
        })
      : this.cookieService.delete(accessTokenCookiesName);

    Array.isArray(refreshTokenTokenCookiesName)
      ? refreshTokenTokenCookiesName.map((value) => {
          this.cookieService.delete(value);
        })
      : this.cookieService.delete(refreshTokenTokenCookiesName);

    this.cookieService.delete(`${env.cookies.accessToken}_amount`);
    this.cookieService.delete(`${env.cookies.refreshToken}_amount`);
  }
}
